import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Diary, DocumentReference, Job, PhotoReference, VEHICLE_LOCATION_OPTIONS, VehicleLocation } from '../../../model/job.model';
import { CurrentUserService } from '../../../services/currentUser.service';
import { User } from '../../../model/user.model';
import { DocumentItem } from '../../../model/document.model';
import {
  CustomerContact,
  CustomerFeedback,
  CustomerVulnerability,
  PotentialBreachOfCode
} from '../../../model/gicop-notifications';
import { MatTab, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { JobService } from '../../../services/job.service';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { TitleCasePipe } from '@angular/common';
import { UserService } from '../../../services/user.service';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { AddDiaryComponent } from '../add-diary/add-diary.component';
import { GalleryCurtainComponent } from '../gallery-curtain/gallery-curtain.component';
import { DocumentsCurtainComponent } from '../documents-curtain/documents-curtain.component';
import { AddCustomerFeedbackComponent } from '../gicop-notifications-modals/add-customer-feedback/add-customer-feedback.component';
import { AddCustomerContactComponent } from '../gicop-notifications-modals/add-customer-contact/add-customer-contact.component';
import { AddPotentialBreachCodeComponent } from '../gicop-notifications-modals/add-potential-breach-code/add-potential-breach-code.component';
import { AddCustomerVulnerabilityComponent } from '../gicop-notifications-modals/add-customer-vulnerability/add-customer-vulnerability.component';
import { SelfAuthoriseComponent } from '../self-authorise/self-authorise.component';
import { BookingComponent } from '../booking/booking.component';
import { InvoiceCurtainComponent } from '../invoice-curtain/invoice-curtain.component';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatCard, MatCardContent } from '@angular/material/card';
import { QuoteCurtainComponent } from '../quote-curtain/quote-curtain.component';
import { AddReturnNoQuoteComponent } from '../add-return-no-quote/add-return-no-quote.component';

@Component({
  selector: 'app-send-to-insco-tabs',
  templateUrl: './send-to-insco-tabs.component.html',
  styleUrls: ['./send-to-insco-tabs.component.scss'],
  providers: [TitleCasePipe],
  imports: [
    MatTabGroup,
    MatTab,
    MatTabLabel,
    MatIcon,
    MatTooltip,
    AddDiaryComponent,
    GalleryCurtainComponent,
    DocumentsCurtainComponent,
    AddCustomerFeedbackComponent,
    AddCustomerContactComponent,
    AddPotentialBreachCodeComponent,
    AddCustomerVulnerabilityComponent,
    SelfAuthoriseComponent,
    BookingComponent,
    InvoiceCurtainComponent,
    MatButton,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatCard,
    MatCardContent,
    QuoteCurtainComponent,
    AddReturnNoQuoteComponent
  ]
})
export class SendToInscoTabsComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('tabGroup', { static: false }) tabGroup: MatTabGroup;
  @Input() job: Job;
  @Input() recipient: string;
  @Input() ownerName: string;
  @Input() unSubmittedDocs: DocumentReference[];
  @Input() unSubmittedPhotos: PhotoReference[] = [];
  @Input() unSubmittedQuote: DocumentItem;
  @Input() unSubmittedInvoice: DocumentItem;
  @Input() noQuoteResponse: boolean;
  @Input() noQuoteResponseAssessmentAction: string;
  @Input() submittedQuoteNotAuthorised: DocumentItem[] = [];
  @Input() submittedQuotes: DocumentItem[] = [];
  @Input() unSubmittedCustomerFeedback: CustomerFeedback;
  @Input() unSubmittedDiary: Diary;
  @Input() unSubmittedCustomerContact: CustomerContact;
  @Input() unSubmittedPotentialBreachOfCode: PotentialBreachOfCode;
  @Input() unSubmittedCustomerVulnerability: CustomerVulnerability;
  @Input() showSelfAuthorisationCurtain: boolean;
  @Input() showValidationGuidance: boolean;
  @Input() indexTab = '';
  @Input() showBooking: boolean;
  @Input() hasQuotes: boolean;
  @Input() expandCurtainInTray = false;
  @Output() refresh = new EventEmitter();
  @Output() updateSelectedPhoto = new EventEmitter();
  @Output() validateSendJob = new EventEmitter();
  @Output() openPdfReport = new EventEmitter();
  @Output() disableSendButton = new EventEmitter();
  @Output() synchronizeOwnerContribution = new EventEmitter();
  @ViewChild('inscoTitle', { static: false }) inscoTitle: ElementRef;
  timezone: string;
  currentUser: User;
  activatedTab = '';
  selectedTabIndex = 0;
  quoteCurtainEnableSendButton = true;
  readonly vehicleLocationOptions = VEHICLE_LOCATION_OPTIONS;

  private tabGroupLength = 0;
  private checkNewTab = new Subject<void>();

  jobService = inject(JobService);
  private currentUserService = inject(CurrentUserService);
  private messageService = inject(MessagingService);
  private titleCasePipe = inject(TitleCasePipe);
  private userService = inject(UserService);

  ngOnInit() {
    this.currentUser = this.currentUserService.currentUserValue;
    this.timezone = this.currentUserService?.timeZone;
    this.checkIfNewTabWasAdded();
    this.shouldDisplayWarningQuoteTab();
  }

  ngAfterViewInit(): void {
    this.tabGroupLength = this.tabGroup?._allTabs.length;
    this.activatedTab = this.tabGroup?._allTabs.first?.textLabel ?? '';
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.indexTab) {
      this.activatedTab = changes?.indexTab?.currentValue;
    }
    // Will update the view before executing the Observable
    this.checkNewTab.next();

    if (changes?.unSubmittedQuote || changes?.submittedQuoteNotAuthorised) {
      this.shouldDisplayWarningQuoteTab();
    }
  }

  private shouldDisplayWarningQuoteTab(): void {
    this.jobService.displayWarningQuoteTab.set(
      !!this.unSubmittedQuote &&
        this.submittedQuoteNotAuthorised?.length > 0 &&
        !this.job.allowSupplementQuote &&
        this.job.updateStrategyWaiting
    );
  }

  private checkIfNewTabWasAdded(): void {
    this.checkNewTab.pipe(debounceTime(0)).subscribe(() => {
      if (this.tabGroupLength !== this.tabGroup?._tabs.length) {
        this.tabGroupLength = this.tabGroup?._tabs.length;
        this.tabGroup?._tabs.forEach((item, index) => {
          item.isActive = this.activatedTab === item?.textLabel;
          if (item.textLabel === this.activatedTab) {
            // If the selectedTabIndex is the same as before it will not update the view with only this.selectedTabIndex = index;
            this.tabGroup.selectedIndex = index;
            this.tabGroup._elementRef.nativeElement.scrollIntoView({
              behavior: 'smooth'
            });
          }
        });
      }
    });
  }

  onVehicleLocationChange(value: VehicleLocation): void {
    this.userService.updateJob(value, this.job.jobId, 'vehicleLocation').subscribe(() => {
      this.job.vehicleLocation = value;
      this.validateSendJob.emit();
    });
  }

  updateStrategyQuote(strategyName: 'REPLACE' | 'MERGE'): void {
    this.jobService.changeUpdateStrategy(this.job.jobId, strategyName).subscribe(() => {
      this.jobService.displayWarningQuoteTab.set(false);
      this.messageService.confirm(`Quote ${this.titleCasePipe.transform(strategyName)}d!`);
      this.refresh.emit('QuoteReplacedOrMerged');
    });
  }

  ngOnDestroy(): void {
    this.checkNewTab.next();
    this.checkNewTab.complete();
    this.jobService.displayWarningQuoteTab.set(false);
  }
}
